import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, CreditCard, Sparkles, RotateCcw, ArrowRight } from "lucide-react";
import { waLink, waCalculatorMsg } from "@/lib/wa";
import { useAuth, trackInterest } from "@/lib/auth";
import { STR } from "@/data/i18n";
import { useLang } from "@/lib/lang";
import { FadeUp, SectionHeading, WhatsAppIcon } from "./Shared";

const Q_ICONS = [Building2, CreditCard, Sparkles];
const PLAN_META = {
    basic: { name: "Basic", price: "Rp 5.000.000" },
    profesional: { name: "Profesional", price: "Rp 10.000.000" },
    luxury: { name: "Luxury", price: "Rp 50.000.000" },
};

const recommend = (a) => {
    if (a.properti === "banyak" || a.lanjutan === "ya") return "luxury";
    if (a.bayar === "ya") return "profesional";
    return "basic";
};

const CalcResultButton = ({ result, t }) => {
    const { user } = useAuth();
    return (
        <a
            href={waLink(waCalculatorMsg(result.name, result.price))}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackInterest(user, result.name)}
            data-testid="calc-result-wa-button"
            className="btn-flame inline-flex flex-1 items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-orange-900/40 transition-transform duration-200 hover:scale-[1.03]"
        >
            <WhatsAppIcon className="h-4 w-4" />
            {t.consult} {result.name}
        </a>
    );
};

export const Calculator = () => {
    const [answers, setAnswers] = useState({});
    const { lang } = useLang();
    const t = STR.calculator[lang];
    const done = t.questions.every((q) => answers[q.id]);
    const resultKey = done ? recommend(answers) : null;
    const result = resultKey ? { ...PLAN_META[resultKey], reasons: t.results[resultKey].reasons } : null;

    return (
        <section id="kalkulator" data-testid="calculator-section" className="relative py-24 md:py-32">
            <div className="mx-auto max-w-3xl px-5 md:px-8">
                <SectionHeading eyebrow={t.eyebrow} title={t.title} subtitle={t.subtitle} />
                <FadeUp>
                    <div className="card-surface space-y-8 p-7 md:p-10">
                        {t.questions.map((q, qi) => {
                            const QIcon = Q_ICONS[qi];
                            return (
                                <fieldset key={q.id} data-testid={`calc-question-${q.id}`}>
                                    <legend className="flex items-start gap-3 text-base font-semibold text-slate-100">
                                        <span className="btn-flame mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
                                            {qi + 1}
                                        </span>
                                        {q.question}
                                    </legend>
                                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:pl-10">
                                        {q.options.map((opt) => {
                                            const active = answers[q.id] === opt.value;
                                            return (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    data-testid={`calc-option-${q.id}-${opt.value}`}
                                                    onClick={() =>
                                                        setAnswers({ ...answers, [q.id]: opt.value })
                                                    }
                                                    className={`rounded-xl border px-5 py-3.5 text-sm font-medium transition-[border-color,background-color,color] duration-200 ${
                                                        active
                                                            ? "border-amber-400/70 bg-amber-400/10 text-amber-300"
                                                            : "border-white/10 bg-white/5 text-zinc-300 hover:border-amber-400/40"
                                                    }`}
                                                >
                                                    {opt.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </fieldset>
                            );
                        })}

                        <AnimatePresence>
                            {result && (
                                <motion.div
                                    key={result.name + lang}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.4 }}
                                    data-testid="calc-result"
                                    className="rounded-2xl border border-orange-500/50 bg-[#161210] p-7 shadow-[0_0_40px_rgba(234,88,12,0.15)]"
                                >
                                    <p className="text-xs font-semibold uppercase tracking-widest text-amber-400">
                                        {t.resultLabel}
                                    </p>
                                    <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                                        <h3 className="text-2xl font-bold text-slate-50">
                                            {t.packageWord} {result.name}
                                        </h3>
                                        <p className="text-gradient text-xl font-extrabold">
                                            {result.price}
                                        </p>
                                    </div>
                                    <ul className="mt-4 space-y-2">
                                        {result.reasons.map((r) => (
                                            <li key={r} className="flex items-start gap-2 text-sm text-zinc-300">
                                                <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                                                {r}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                        <CalcResultButton result={result} t={t} />
                                        <button
                                            type="button"
                                            data-testid="calc-reset-button"
                                            onClick={() => setAnswers({})}
                                            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-medium text-zinc-300 transition-colors duration-200 hover:border-amber-400/50 hover:text-amber-300"
                                        >
                                            <RotateCcw className="h-4 w-4" />
                                            {t.reset}
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </FadeUp>
            </div>
        </section>
    );
};
